/**
 * ExternalDiffsListTab - MVP version
 * Simplified diffs list that fetches from external API
 * Reuses DiffCard and DiffEventLightbox components
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '@/store/store';
import {
  setExternalDiffsList,
  appendExternalDiffs,
  incrementApiRequestCount,
  setExternalNavigationList,
  setExternalDiffsFilters,
  isCacheValid,
  clearExternalDiffsCache,
  updateExternalDiffInList,
} from '@/store/externalDiffsSlice';
import { fetchDiffs, invalidateAnnotationCache, toggleDiffHighlight, setDiffPublished } from '@/utils/externalDiffsAPI';
import { transformExternalDiffs, stripAnnotationsForList } from '@/utils/externalDiffsTransformer';
import type { Diff } from '@/utils/externalDiffsTransformer';
import { getAllEventTypes, getCategoryForEventType } from '@/constants/eventTypeCategories';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertCircle,
  Loader2,
  X,
  ChevronDown,
  SlidersHorizontal,
  Search,
  Star,
  Eye,
  EyeOff,
} from 'lucide-react';
import { EventTypeBadge } from '@/components/diffs/EventTypeBadge';
import {
  EVENT_TYPE_CATEGORIES,
  CATEGORY_METADATA,
  isCategoryFullySelected,
  type EventCategory,
} from '@/constants/eventTypeCategories';

interface ExternalDiffsListTabProps {
  periodFilter: string;
  companySlug?: string;
  searchInput?: string;
  onSearchInputChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  onSearchClear?: () => void;
  statusFilter?: 'all' | 'published' | 'qa_checked' | 'qa_pending' | 'archived';
  sortBy?: 'created_at' | 'qa_check';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Format version date in abbreviated format (e.g., "Oct 5, 2025")
 */
const formatVersionDateAbbreviated = (version: string): string => {
  const dateMatch = version.match(/(\d{8})/);
  if (!dateMatch) return '';

  const dateStr = dateMatch[1];
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)); // 1-indexed from API
  const day = parseInt(dateStr.substring(6, 8));   // 1-indexed from API

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Convert 1-indexed month to 0-indexed for array access
  return `${monthNames[month - 1]} ${day}, ${year}`;
};


const PAGE_SIZE = 50;

export const ExternalDiffsListTab: React.FC<ExternalDiffsListTabProps> = ({
  periodFilter,
  companySlug = '',
  searchInput = '',
  onSearchInputChange,
  onSearchSubmit,
  onSearchClear,
  statusFilter = 'all',
  sortBy = 'created_at',
  sortOrder = 'desc',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redux state
  const cachedList = useSelector((state: RootState) => state.externalDiffs.listCache);
  const navigationList = useSelector((state: RootState) => state.externalDiffs.navigationList);

  // Local state
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [showInvalid, setShowInvalid] = useState(() => {
    // Load from localStorage, default to false (hide invalid)
    const saved = localStorage.getItem('diffs2_show_invalid');
    return saved === 'true';
  });

  // Event type filter state — committed state drives API calls
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('diffs2_event_filter');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // Draft state for in-dropdown editing (avoids API call per checkbox click)
  const [draftEventTypes, setDraftEventTypes] = useState<Set<string>>(() => new Set(selectedEventTypes));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Derive active event types array (memoized to prevent unnecessary re-renders)
  const eventTypes = useMemo(() => Array.from(selectedEventTypes), [selectedEventTypes]);

  // Sync event types to localStorage
  useEffect(() => {
    if (selectedEventTypes.size > 0) {
      localStorage.setItem('diffs2_event_filter', JSON.stringify(Array.from(selectedEventTypes)));
    } else {
      localStorage.removeItem('diffs2_event_filter');
    }
  }, [selectedEventTypes]);

  // When filter dropdown opens, copy committed state to draft
  // When it closes, commit draft to actual state (triggers single API call)
  const handleFilterOpenChange = (open: boolean) => {
    if (open) {
      setDraftEventTypes(new Set(selectedEventTypes));
    } else {
      // Only update if draft differs from committed
      const draftArr = Array.from(draftEventTypes).sort();
      const committedArr = Array.from(selectedEventTypes).sort();
      if (JSON.stringify(draftArr) !== JSON.stringify(committedArr)) {
        setSelectedEventTypes(new Set(draftEventTypes));
      }
    }
    setIsFilterOpen(open);
  };

  // Calculate selected categories from the set currently shown in UI
  const displayedEventTypes = isFilterOpen ? draftEventTypes : selectedEventTypes;
  const selectedCategories = new Set<EventCategory>();
  (['pricing', 'packaging', 'product'] as EventCategory[]).forEach(category => {
    if (isCategoryFullySelected(category, displayedEventTypes)) {
      selectedCategories.add(category);
    }
  });

  // Modal state removed - navigation now uses detail page


  // Use cached data
  const allDiffs = cachedList?.data.diffs || [];
  const totalCount = cachedList?.data.totalCount || 0;
  const lastFetched = cachedList?.lastFetched;

  // Filter diffs based on showInvalid toggle
  // A diff is invalid if ALL its events are invalid
  const diffs = showInvalid ? allDiffs : allDiffs.filter(diff => {
    // Show diff if it has at least one non-invalid event
    if (!diff.changes || diff.changes.length === 0) return true;
    return diff.changes.some(event => !event.invalid);
  });

  /**
   * Handle category checkbox toggle
   */
  const handleCategoryToggle = (category: EventCategory) => {
    const categoryTypes = EVENT_TYPE_CATEGORIES[category];
    const isCategorySelected = isCategoryFullySelected(category, draftEventTypes);

    const newDraft = new Set(draftEventTypes);

    if (isCategorySelected) {
      categoryTypes.forEach(type => newDraft.delete(type));
    } else {
      categoryTypes.forEach(type => newDraft.add(type));
    }

    setDraftEventTypes(newDraft);
  };

  /**
   * Handle individual event type toggle (operates on draft)
   */
  const handleEventTypeToggle = (eventType: string) => {
    const newDraft = new Set(draftEventTypes);

    if (newDraft.has(eventType)) {
      newDraft.delete(eventType);
    } else {
      newDraft.add(eventType);
    }

    setDraftEventTypes(newDraft);
  };

  /**
   * Clear all event type filters
   */
  const handleClearEventTypes = () => {
    setDraftEventTypes(new Set());
    // If not inside the dropdown, also commit immediately
    if (!isFilterOpen) {
      setSelectedEventTypes(new Set());
    }
  };

  /**
   * Format event type labels (convert snake_case to Title Case)
   */
  const formatEventTypeLabel = (eventType: string): string => {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  /**
   * Load diffs from external API
   */
  const loadDiffs = async (offset: number = 0, resetList: boolean = true) => {
    const isInitialLoad = resetList;

    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      // Derive published and qa_status from combined statusFilter
      const publishedParam = statusFilter === 'published' ? true :
                             statusFilter === 'qa_checked' || statusFilter === 'qa_pending' || statusFilter === 'archived' ? false :
                             undefined;
      // 'archived' = QA looked and concluded the evidence can never support a claim (blank render,
      // wrong-company capture, consent modal over the price row). The historical screenshot cannot
      // be retaken, so it is terminal — it is deliberately NOT part of qa_pending.
      const qaStatusParam = statusFilter === 'qa_checked' ? 'checked' as const :
                            statusFilter === 'qa_pending' ? 'pending' as const :
                            statusFilter === 'archived' ? 'archived' as const :
                            undefined;

      // Build API params
      const params = {
        slug: companySlug || undefined,
        period: periodFilter || undefined,
        event_types: eventTypes && eventTypes.length > 0 ? eventTypes : undefined,
        published: publishedParam,
        qa_status: qaStatusParam,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: PAGE_SIZE,
        offset,
      };

      // Fetch from API
      const response = await fetchDiffs(params);

      // Track API requests
      const requestCount = response.requestsMade || 1;
      for (let i = 0; i < requestCount; i++) {
        dispatch(incrementApiRequestCount({
          endpoint: companySlug ? `/api/diffs/${companySlug}` : '/api/diffs/all',
          params: {},
        }));
      }

      // Transform data and strip heavy annotations for list caching
      const transformedDiffs = stripAnnotationsForList(transformExternalDiffs(response.data));

      // Update Redux state
      if (isInitialLoad) {
        dispatch(setExternalDiffsList({
          diffs: transformedDiffs,
          totalCount: response.totalCount,
          currentPage: 1,
          hasNextPage: transformedDiffs.length >= PAGE_SIZE,
          hasPrevPage: false,
          periodFilter,
          slug: companySlug,
          event_types: eventTypes,
          published: publishedParam,
          qa_status: qaStatusParam,
          sort_by: sortBy,
          sort_order: sortOrder,
        }));

        // Set navigation list for prev/next navigation
        dispatch(setExternalNavigationList(transformedDiffs.map(d => d.id)));

        // Store current filters for navigation context
        dispatch(setExternalDiffsFilters({
          slug: companySlug,
          period: periodFilter,
          event_types: eventTypes,
          status_filter: statusFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
        }));

        setCurrentOffset(PAGE_SIZE);
        setHasMoreEvents(transformedDiffs.length >= PAGE_SIZE);
      } else {
        dispatch(appendExternalDiffs({
          diffs: transformedDiffs,
          hasNextPage: transformedDiffs.length >= PAGE_SIZE,
        }));

        // Update navigation list with newly loaded diff IDs
        dispatch(setExternalNavigationList([
          ...(navigationList || []),
          ...transformedDiffs.map(d => d.id),
        ]));

        setCurrentOffset(prev => prev + PAGE_SIZE);
        setHasMoreEvents(transformedDiffs.length >= PAGE_SIZE);
      }

    } catch (err: any) {
      console.error('[ExternalDiffsListTab] Error loading diffs:', err);
      setError(err.message || 'Failed to load diffs');
      toast({
        title: 'Error loading diffs',
        description: err.message || 'Failed to fetch data from external API',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  /**
   * Load more diffs (pagination)
   */
  const handleLoadMore = () => {
    if (!loadingMore && hasMoreEvents) {
      loadDiffs(currentOffset, false);
    }
  };

  /**
   * Navigate to detail page for a diff
   */
  const handleNavigateToDetail = (diff: Diff) => {
    const diffIndex = cachedList?.data.diffs.findIndex(d => d.id === diff.id) ?? -1;
    navigate(`/diffs2/${diff.slug}/${periodFilter || diff.period}?index=${diffIndex}`);
  };

  /**
   * Toggle show/hide invalid events
   */
  const handleToggleShowInvalid = (checked: boolean) => {
    setShowInvalid(checked);
    localStorage.setItem('diffs2_show_invalid', checked.toString());
  };

  /**
   * Toggle highlight flag for a diff
   */
  const handleToggleHighlight = async (e: React.MouseEvent, diff: Diff) => {
    e.stopPropagation(); // Prevent navigation to detail page

    const newHighlightValue = !diff.highlight;

    // Optimistic update
    const updatedDiff = { ...diff, highlight: newHighlightValue };
    dispatch(updateExternalDiffInList(updatedDiff));

    try {
      await toggleDiffHighlight(diff.id, newHighlightValue);

      toast({
        title: newHighlightValue ? 'Diff highlighted' : 'Highlight removed',
        description: `${diff.slug} has been ${newHighlightValue ? 'highlighted' : 'unhighlighted'}`,
      });
    } catch (error: any) {
      // Revert optimistic update on error
      dispatch(updateExternalDiffInList(diff));

      toast({
        title: 'Error updating highlight',
        description: error.message || 'Failed to update highlight status',
        variant: 'destructive',
      });
    }
  };

  /**
   * Toggle published flag for a diff
   */
  const handleTogglePublished = async (e: React.MouseEvent, diff: Diff) => {
    e.stopPropagation();

    const newPublishedValue = !diff.published;

    // Optimistic update
    const updatedDiff = { ...diff, published: newPublishedValue };
    dispatch(updateExternalDiffInList(updatedDiff));

    try {
      await setDiffPublished(diff.id, newPublishedValue);

      toast({
        title: newPublishedValue ? 'Diff published' : 'Diff unpublished',
        description: `${diff.slug} is now ${newPublishedValue ? 'published' : 'a draft'}`,
      });
    } catch (error: any) {
      // Revert optimistic update on error
      dispatch(updateExternalDiffInList(diff));

      toast({
        title: 'Error updating published status',
        description: error.message || 'Failed to update published status',
        variant: 'destructive',
      });
    }
  };

  /**
   * Effect: Load diffs when filters change
   */
  useEffect(() => {
    // Don't load if no period is selected (prevents loading metadata endpoint)
    if (!periodFilter && !companySlug) {
      return;
    }

    // Derive API params from statusFilter for cache check
    const publishedParam = statusFilter === 'published' ? true :
                           statusFilter === 'qa_checked' || statusFilter === 'qa_pending' ? false :
                           undefined;
    const qaStatusParam = statusFilter === 'qa_checked' ? 'checked' as const :
                          statusFilter === 'qa_pending' ? 'pending' as const :
                          undefined;

    // Check cache validity
    const cacheIsValid = isCacheValid(
      cachedList,
      periodFilter,
      companySlug,
      eventTypes,
      publishedParam,
      qaStatusParam,
      sortBy,
      sortOrder
    );

    if (cacheIsValid) {
      return;
    }

    loadDiffs(0, true);
  }, [periodFilter, companySlug, eventTypes, statusFilter, sortBy, sortOrder]);

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Loading diffs from external API...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error && diffs.length === 0) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Failed to Load Diffs</h3>
              <p className="text-sm text-red-800 mb-4">{error}</p>
              <Button
                onClick={() => loadDiffs(0, true)}
                variant="outline"
                size="sm"
              >
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  /**
   * Render empty state
   */
  if (diffs.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-gray-600">
            No diffs found for the selected filters.
          </p>
          {selectedEventTypes.size > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block">
                Event type filter is active ({selectedEventTypes.size} type{selectedEventTypes.size === 1 ? '' : 's'} selected) — this may be hiding results.
              </p>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSelectedEventTypes(new Set()); setDraftEventTypes(new Set()); }}
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear event type filter
                </Button>
              </div>
            </div>
          )}
          {selectedEventTypes.size === 0 && (periodFilter || companySlug) && (
            <p className="text-sm text-gray-500 mt-2">
              Try adjusting your filters or search criteria.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  /**
   * Render diffs list
   */
  return (
    <div>
      {/* Count and filters */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {diffs.length} of {totalCount} {totalCount === 1 ? 'diff' : 'diffs'}
          {!showInvalid && allDiffs.length > diffs.length && (
            <span className="text-gray-500 ml-2">
              ({allDiffs.length - diffs.length} hidden)
            </span>
          )}
        </p>

        <div className="flex items-center gap-2">
          {/* Company Search */}
          {companySlug ? (
            // Active search - show badge with clear button
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-md">
              <Search className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs text-blue-900 font-medium">{companySlug}</span>
              <button
                onClick={onSearchClear}
                className="text-blue-600 hover:text-blue-800"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : onSearchInputChange && onSearchSubmit ? (
            // Search input - compact inline version
            <div className="flex items-center gap-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search company..."
                  value={searchInput}
                  onChange={(e) => onSearchInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchInput.trim()) {
                      onSearchSubmit();
                    }
                  }}
                  className="h-8 w-40 pl-7 pr-2 text-xs"
                />
              </div>
              <Button
                onClick={onSearchSubmit}
                variant="ghost"
                size="sm"
                disabled={!searchInput.trim()}
                className="h-8 px-2 text-xs"
              >
                Search
              </Button>
            </div>
          ) : null}

          {/* Filters Dropdown */}
          <DropdownMenu open={isFilterOpen} onOpenChange={handleFilterOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 gap-2 text-sm font-normal ${selectedEventTypes.size > 0 ? 'text-blue-700 bg-blue-50 hover:bg-blue-100' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {selectedEventTypes.size > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs bg-blue-100 text-blue-800">
                    {selectedEventTypes.size}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[380px] p-4">
            <div className="space-y-4">
              {/* Show Invalid Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Show Invalid Events</span>
                <Switch
                  checked={showInvalid}
                  onCheckedChange={handleToggleShowInvalid}
                />
              </div>

              <DropdownMenuSeparator />

              {/* Event Type Filter Header */}
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Event Types</h4>
                {draftEventTypes.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearEventTypes}
                    className="h-7 px-2 text-xs"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {draftEventTypes.size === 0 ? 'Showing all event types' : `Showing ${draftEventTypes.size} event type${draftEventTypes.size === 1 ? '' : 's'} — changes apply when menu closes`}
              </p>

              {/* Category checkboxes */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase">
                  Categories
                </p>
                {(['pricing', 'packaging', 'product'] as EventCategory[]).map(category => {
                  const metadata = CATEGORY_METADATA[category];
                  const isChecked = selectedCategories.has(category);

                  return (
                    <div
                      key={category}
                      className="flex items-center space-x-2 py-1 hover:bg-gray-50 rounded px-2 -mx-2"
                    >
                      <Checkbox
                        id={`category-${category}`}
                        checked={isChecked}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <label
                        htmlFor={`category-${category}`}
                        className="text-sm font-medium cursor-pointer flex-1"
                      >
                        {metadata.label} ({metadata.count})
                      </label>
                    </div>
                  );
                })}
              </div>

              {/* Individual event types - Accordion */}
              <Accordion type="single" collapsible className="w-full">
                {(['pricing', 'packaging', 'product'] as EventCategory[]).map(category => {
                  const metadata = CATEGORY_METADATA[category];
                  const categoryTypes = EVENT_TYPE_CATEGORIES[category];

                  return (
                    <AccordionItem key={category} value={category} className="border-0">
                      <AccordionTrigger className="text-sm font-medium hover:no-underline py-2">
                        {metadata.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1 pl-4">
                          {categoryTypes.map(eventType => {
                            const isChecked = draftEventTypes.has(eventType);

                            return (
                              <div
                                key={eventType}
                                className="flex items-center space-x-2 py-1 hover:bg-gray-50 rounded px-2 -mx-2"
                              >
                                <Checkbox
                                  id={`event-${eventType}`}
                                  checked={isChecked}
                                  onCheckedChange={() => handleEventTypeToggle(eventType)}
                                />
                                <label
                                  htmlFor={`event-${eventType}`}
                                  className="text-sm cursor-pointer flex-1"
                                >
                                  {formatEventTypeLabel(eventType)}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Active event type filter indicator */}
      {selectedEventTypes.size > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">Event types:</span>
          {Array.from(selectedEventTypes).slice(0, 5).map(type => (
            <Badge
              key={type}
              variant="outline"
              className="text-xs px-2 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
            >
              {formatEventTypeLabel(type)}
            </Badge>
          ))}
          {selectedEventTypes.size > 5 && (
            <span className="text-xs text-gray-500">+{selectedEventTypes.size - 5} more</span>
          )}
          <button
            onClick={() => { setSelectedEventTypes(new Set()); setDraftEventTypes(new Set()); }}
            className="text-xs text-blue-600 hover:text-blue-800 underline ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Diffs table/list */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-2">
            {diffs.map(diff => {
              const logoUrl = diff.page?.company?.logo_url;

              const validEvents = showInvalid
                ? (diff.changes || [])
                : (diff.changes?.filter(change => !change.invalid) || []);

              // Get unique event types for icon badges
              const uniqueEventTypes = Array.from(
                new Set(validEvents.map(change => change.event_type).filter(Boolean))
              );

              return (
                <div
                  key={diff.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 cursor-pointer transition-all"
                  onClick={() => handleNavigateToDetail(diff)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {/* Company logo */}
                      {logoUrl && (
                        <img
                          src={logoUrl}
                          alt={`${diff.slug} logo`}
                          className="h-10 w-10 rounded object-contain flex-shrink-0 bg-white border border-gray-200"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        {/* Company name and date range on same line */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">{diff.slug}</span>
                          {diff.v_before && diff.v_after && (
                            <span className="text-xs text-gray-500">
                              {formatVersionDateAbbreviated(diff.v_before)} → {formatVersionDateAbbreviated(diff.v_after)}
                            </span>
                          )}
                        </div>

                        {/* Hook/title */}
                        {diff.hook && (
                          <p className="text-sm text-gray-700 mb-1">{diff.hook}</p>
                        )}

                        {/* Description */}
                        {diff.description && (
                          <p className="text-xs text-gray-500 mb-2">{diff.description}</p>
                        )}

                        {/* Event type badges inline */}
                        {uniqueEventTypes.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {uniqueEventTypes.map(eventType => (
                              <EventTypeBadge key={eventType} eventType={eventType} size="sm" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* QA badge */}
                      {diff.qa_check ? (
                        <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 pointer-events-none">
                          ✓ QA
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground pointer-events-none">
                          Pending QA
                        </Badge>
                      )}

                      {/* Published badge indicator */}
                      {diff.published ? (
                        <Badge className="text-xs bg-green-100 text-green-700 border-green-200 pointer-events-none">
                          ● Published
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-400 border-gray-200 pointer-events-none">
                          ○ Draft
                        </Badge>
                      )}

                      {/* Publish Eye toggle */}
                      <button
                        onClick={(e) => handleTogglePublished(e, diff)}
                        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                        title={diff.published ? 'Unpublish this diff' : 'Publish this diff'}
                      >
                        {diff.published ? (
                          <Eye className="h-4 w-4 text-green-600" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>

                      {/* Event count badge */}
                      {diff.changes && diff.changes.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {diff.changes.length} {diff.changes.length === 1 ? 'event' : 'events'}
                        </Badge>
                      )}

                      {/* Highlight star toggle */}
                      <button
                        onClick={(e) => handleToggleHighlight(e, diff)}
                        className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                        title={diff.highlight ? 'Remove highlight' : 'Highlight this diff'}
                      >
                        <Star
                          className={`h-5 w-5 ${
                            diff.highlight
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-400 hover:text-yellow-400'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Load More button */}
      {hasMoreEvents && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={handleLoadMore}
            disabled={loadingMore}
            variant="outline"
            size="lg"
          >
            {loadingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading more... ({diffs.length} / {totalCount})
              </>
            ) : (
              <>Load More ({diffs.length} / {totalCount})</>
            )}
          </Button>
        </div>
      )}

    </div>
  );
};
